/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { loadUserManager } from "app/client/lib/imports";
import { isKnetEmbed } from "app/client/lib/knetEmbed";
import { makeT } from "app/client/lib/localization";
import { urlState } from "app/client/models/gristUrlState";
import { HomeModel } from "app/client/models/HomeModel";
import { addNewButton, cssAddNewButton } from "app/client/ui/AddNewButton";
import { getAdminPanelName } from "app/client/ui/AdminPanelName";
import { rememberSettingsWorkspace } from "app/client/ui/KnetSettingsPage";
import {
  createAccessibilityTools,
  createHelpTools,
  cssHomeTools,
  cssLeftPanel,
  cssLinkText,
  cssPageButton,
  // cssPageColorIcon,
  cssPageEntry,
  cssPageIcon,
  cssPageLink,
  cssPageLinkContainer,
  cssScrollPane,
  cssSectionHeader,
  cssSectionHeaderText,
  cssTools,
} from "app/client/ui/LeftPanelCommon";
import { newDocMethods } from "app/client/ui/NewDocMethods";
import { createVideoTourToolsButton } from "app/client/ui/OpenVideoTour";
import { railCard } from "app/client/ui/RailCard";
import { transientInput } from "app/client/ui/transientInput";
import { testId, theme } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { knetIconTile, knetNavIconTile, navOverviewTile } from "app/client/ui2018/knetIcons";
import { menu, menuIcon, menuItem, upgradableMenuItem, upgradeText } from "app/client/ui2018/menus";
import { confirmModal } from "app/client/ui2018/modals";
import { commonUrls, isFeatureEnabled } from "app/common/gristUrls";
import * as roles from "app/common/roles";
import { getGristConfig } from "app/common/urlUtils";
import { Workspace } from "app/common/UserAPI";
// import * as version from "app/common/version";

import { computed, Computed, dom, domComputed, DomElementArg, observable, Observable, styled } from "grainjs";

const t = makeT("HomeLeftPane");

export function createHomeLeftPane(leftPanelOpen: Observable<boolean>, home: HomeModel) {
  const creating = observable<boolean>(false);
  const renaming = observable<Workspace | null>(null);
  const isAnonymous = !home.app.currentValidUser;
  const { enableAnonPlayground, templateOrg, onboardingTutorialDocId } = getGristConfig();
  const canCreate = !isAnonymous || enableAnonPlayground;

  // Show version when hovering over the application icon.
  // const appVersion = `Version ${version.version}` +
  //   ((version.gitcommit as string) !== "unknown" ? ` (${version.gitcommit})` : "");

  const channelsExpanded = observable<boolean>(false);
  const workspacesExpandedStatus = new Map<number, Observable<boolean>>();

  function getWsExpanded(id: number) {
    if (!workspacesExpandedStatus.has(id)) {
      workspacesExpandedStatus.set(id, observable(true));
    }
    return workspacesExpandedStatus.get(id)!;
  }

  function collapsibleHeader(label: string, expanded: Observable<boolean>, testIdStr: string) {
    return cssSectionHeader(
      cssSectionHeaderText(t(label)),
      isAnonymous ? null : cssToggleIcon(
        dom.domComputed(expanded, isExpanded => icon(isExpanded ? "Minus" : "Plus")),
        dom.on("click", (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          expanded.set(!expanded.get());
        }),
        testId(testIdStr + "-toggle"),
      ),
      { id: `knetai-${label.toLowerCase()}-heading` },
      testId(testIdStr),
    );
  }

  return cssContent(
    dom.autoDispose(creating),
    dom.autoDispose(renaming),
    addNewButton({ isOpen: leftPanelOpen, isDisabled: !canCreate },
      canCreate ? menu(() => addMenu(home, creating), {
        placement: "bottom-start",
        // "Add New" menu should have the same width as the "Add New" button that opens it.
        stretchToSelector: `.${cssAddNewButton.className}`,
      }) : null,
      dom.cls("behavioral-prompt-add-new"),
      testId("dm-add-new"),
    ),
    cssScrollPane(
      // Fold the main nav like the bottom tools: hides labels/section headers and
      // centers icons when the panel is collapsed to the 48px rail.
      dom.cls(`${cssTools.className}-collapsed`, use => !use(leftPanelOpen)),
      // --- OVERVIEW Link (Mission Control) ---
      // Show Overview only in Mission Control mode and when not embedded in Kue
      dom.maybe(use => !use(home.onlyShowDocuments) && !isKnetEmbed(), () =>
        cssPageEntry(
          cssPageEntry.cls("-selected", use => use(home.currentPage) === "all" && !use(home.onlyShowDocuments)),
          isAnonymous ? [
            dom.cls("-disabled"),
            cssPageLinkContainer(navOverviewTile(), cssLinkText(t("Overview")), testId("dm-overview"), railCard("dm-overview")),
          ] : [
            cssPageLink(navOverviewTile(),
              cssLinkText(t("Overview")),
              urlState().setLinkUrl({ ws: undefined, homePage: undefined }),
              dom.on("click", () => home.onlyShowDocuments.set(false)),
              testId("dm-overview"), railCard("dm-overview"),
            ),
          ],
        ),
      ),
      /*
      dom.forEach(home.configuredAppConnectors, (connector) =>
        cssPageEntry(
          cssPageLink(cssPageKnetIcon("connectors"), cssLinkText(connector),
            { href: `/connect/${connector}`, target: "_blank" },
            testId("dm-connector-item"),
          ),
        )
      ),
      */

      // --- THE SELECTED WORKSPACE ---
      // Only the one selected workspace, not every workspace of the site
      // (hermes-agent-ui openspec one-selected-workspace).
      dom.forEach(computed((use) => {
        const selected = use(home.selectedWS);
        return selected ? [selected] : [];
      }), (ws) => {
        const wsId = ws.id;
        const wsExpanded = getWsExpanded(wsId);
        const wsName = ws.name;
        const isRenaming = Computed.create(null, use => use(renaming)?.id === wsId);

        return cssWsSection(
          dom.maybe(isRenaming, () => cssPageEntry(
            cssPageIcon("Folder"),
            cssEditorInput({
              initialValue: wsName,
              save: async val => (val !== wsName) ? home.renameWorkspace(wsId, val) : undefined,
              close: () => renaming.set(null),
            }, testId("dm-ws-name-editor")),
          )),
          dom.maybe(use => !use(isRenaming), () => [
            cssSectionHeader(
              cssWorkspaceHeaderLink(
                t(wsName),
                isAnonymous ? null : dom.on("click", (ev) => {
                  ev.preventDefault();
                  home.workspaceFilter.set(wsId);
                  urlState().pushUrl(prev => ({ org: prev.org, homePageTab: "recent" as any })).catch(() => {});
                }),
                isAnonymous ? null : dom.style("cursor", "pointer"),
              ),
              isAnonymous ? null : [
                cssDotsIcon(
                  icon("Dots"),
                  menu(() => workspaceMenu(home, ws, renaming), {
                    placement: "bottom-end",
                  }),
                  testId("dm-workspace-options"),
                ),
                cssToggleIcon(
                  dom.domComputed(wsExpanded, isExpanded => icon(isExpanded ? "Minus" : "Plus")),
                  dom.on("click", (ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    wsExpanded.set(!wsExpanded.get());
                  }),
                  testId("dm-workspace-header-toggle"),
                ),
              ],
              testId("dm-workspace-header"),
            ),
            dom.maybe(wsExpanded, () => [
              // Databases — the core database service of the workspace
              cssSubEntry(
                cssSubEntry.cls("-selected", use => (
                  use(home.onlyShowDocuments) ?
                    (use(home.currentWSId) === ws.id || use(home.currentPage) === "all") :
                    use(home.currentWSId) === ws.id
                )),
                cssPageLink(knetNavIconTile("databases"), cssLinkText(t("Databases")),
                  urlState().setLinkUrl({ ws: ws.id, homePageTab: "all" } as any),
                  dom.on("click", () => home.onlyShowDocuments.set(true)),
                  testId("dm-ws-databases"), railCard("dm-ws-databases"),
                ),
              ),
              // Other services — only in Mission Control mode (!onlyShowDocuments) and not embedded in Kue
              dom.maybe(use => !use(home.onlyShowDocuments) && !isKnetEmbed(), () => [
                // 1. Agents (Knet Agents)
                cssSubEntry(
                  isAnonymous ? [
                    dom.cls("-disabled"),
                    cssPageLinkContainer(knetNavIconTile("knets"), cssLinkText(t("K-Nets")), testId("dm-ws-agents"), railCard("dm-ws-agents")),
                  ] : [
                    cssPageLink(knetNavIconTile("knets"), cssLinkText(t("K-Nets")),
                      { href: `/flows/o/${home.effectiveOrgId}/ws/${wsId}/knets/`,
                        target: "_blank" },
                      testId("dm-ws-agents"), railCard("dm-ws-agents"),
                    ),
                  ],
                ),
                // 2. Knowledge Bases
                cssSubEntry(
                  isAnonymous ? [
                    dom.cls("-disabled"),
                    cssPageLinkContainer(knetNavIconTile("knowledgebases"), cssLinkText(t("Knowledge Bases")), testId("dm-ws-kb"), railCard("dm-ws-kb")),
                  ] : [
                    cssPageLink(knetNavIconTile("knowledgebases"), cssLinkText(t("Knowledge Bases")),
                      { href: `/knowledgebases/o/${home.effectiveOrgId}/ws/${wsId}/collections/`, target: "_blank" },
                      testId("dm-ws-kb"), railCard("dm-ws-kb"),
                    ),
                  ],
                ),
                // 3. Apps / Connectors
                cssSubEntry(
                  isAnonymous ? [
                    dom.cls("-disabled"),
                    cssPageLinkContainer(knetNavIconTile("connectors"), cssLinkText(t("Connectors")), testId("dm-ws-apps"), railCard("dm-ws-apps")),
                  ] : [
                    cssPageLink(knetNavIconTile("connectors"), cssLinkText(t("Connectors")),
                      { href: `/connectors/o/${home.effectiveOrgId}/ws/${wsId}/appconfigs`, target: "_blank" },
                      testId("dm-ws-apps"), railCard("dm-ws-apps"),
                    ),
                  ],
                ),
                // 4. Maps
                cssSubEntry(
                  isAnonymous ? [
                    dom.cls("-disabled"),
                    cssPageLinkContainer(knetNavIconTile("maps"), cssLinkText(t("Maps")), testId("dm-ws-maps"), railCard("dm-ws-maps")),
                  ] : [
                    cssPageLink(knetNavIconTile("maps"), cssLinkText(t("Maps")),
                      { href: `/maps/o/${home.effectiveOrgId}/ws/${wsId}/`, target: "_blank" },
                      testId("dm-ws-maps"), railCard("dm-ws-maps"),
                    ),
                  ],
                ),
                // 5. Crawlers
                cssSubEntry(
                  isAnonymous ? [
                    dom.cls("-disabled"),
                    cssPageLinkContainer(knetNavIconTile("crawlers"), cssLinkText(t("Crawlers")), testId("dm-ws-crawlers"), railCard("dm-ws-crawlers")),
                  ] : [
                    cssPageLink(knetNavIconTile("crawlers"), cssLinkText(t("Crawlers")),
                      { href: `/crawlers/o/${home.effectiveOrgId}/ws/${wsId}/crawlers/`, target: "_blank" },
                      testId("dm-ws-crawlers"), railCard("dm-ws-crawlers"),
                    ),
                  ],
                ),
              ]),
            ]),
          ]),
        );
      }),

      dom.maybe(creating, () => cssPageEntry(
        cssPageLink(cssPageIcon("Folder"),
          cssEditorInput({
            initialValue: "",
            save: async val => (val !== "") ? home.createWorkspace(val) : undefined,
            close: () => creating.set(false),
          }, testId("dm-ws-name-editor")),
        ),
      )),

      // --- Section: CHANNELS (Global) ---
      // Show channels only when in Mission Control mode (!onlyShowDocuments) and not embedded in Kue.
      dom.maybe(use => !use(home.onlyShowDocuments) && !isKnetEmbed(), () => [
        collapsibleHeader("Channels", channelsExpanded, "dm-channels-label"),
        dom.maybe(channelsExpanded, () => [
          cssPageEntry(
            isAnonymous ? [
              dom.cls("-disabled"),
              cssPageLinkContainer(knetNavIconTile("chat"), cssLinkText(t("Chat")), testId("dm-chat"), railCard("dm-chat")),
            ] : [
              cssPageLink(knetNavIconTile("chat"), cssLinkText(t("Chat")),
                { href: "/chats", target: "_blank" },
                testId("dm-chat"), railCard("dm-chat"),
              ),
            ],
          ),
          cssPageEntry(
            isAnonymous ? [
              dom.cls("-disabled"),
              cssPageLinkContainer(knetNavIconTile("api"), cssLinkText(t("API")), testId("dm-api"), railCard("dm-api")),
            ] : [
              cssPageLink(knetNavIconTile("api"), cssLinkText(t("API")),
                { href: "/api/", target: "_blank" },
                testId("dm-api"), railCard("dm-api"),
              ),
            ],
          ),
          cssPageEntry(
            isAnonymous ? [
              dom.cls("-disabled"),
              cssPageLinkContainer(knetNavIconTile("whatsapp"), cssLinkText(t("Whatsapp")), testId("dm-whatsapp"), railCard("dm-whatsapp")),
            ] : [
              cssPageLink(knetNavIconTile("whatsapp"), cssLinkText(t("Whatsapp")),
                { href: "/whatsapp/manager/", target: "_blank" },
                testId("dm-whatsapp"), railCard("dm-whatsapp"),
              ),
            ],
          ),
          cssPageEntry(
            isAnonymous ? [
              dom.cls("-disabled"),
              cssPageLinkContainer(knetNavIconTile("voice"), cssLinkText(t("Voice")), testId("dm-voice"), railCard("dm-voice")),
            ] : [
              cssPageLink(knetNavIconTile("voice"), cssLinkText(t("Voice")),
                { href: "/voice/", target: "_blank" },
                testId("dm-voice"), railCard("dm-voice"),
              ),
            ],
          ),
        ]),
      ]),

      cssHomeTools(
        // { "aria-labelledby": "knetai-resources-heading" },
        // cssSectionHeader(
        //   cssPageColorIcon("GristLogo", { title: appVersion, id: "knetai-resources-logo" }),
        //   cssSectionHeaderText(t("Knet Resources"), { id: "knetai-resources-heading" }),
        // ),
        // Local Kue (KnetDB's own assistant) is hidden for now; the platform Kue is the assistant.
        // dom.maybe(getGristConfig().assistant, () =>
        //   cssPageEntry(
        //     isAnonymous ? [
        //       dom.cls("-disabled"),
        //       cssPageLinkContainer(cssPageKnetIcon("kue"), cssLinkText(t("Kue")), testId("dm-assistant"), railCard("dm-assistant")),
        //     ] : [
        //       cssPageLink(cssPageKnetIcon("kue"), cssLinkText(t("Kue")),
        //         dom.on("click", () => home.app.openGlobalAssistant()),
        //         testId("dm-assistant"), railCard("dm-assistant"),
        //       ),
        //     ],
        //   ),
        // ),
        cssPageEntry(
          dom.show(isFeatureEnabled("templates") && Boolean(templateOrg)),
          cssPageEntry.cls("-selected", use => use(home.currentPage) === "templates"),
          isAnonymous ? [
            dom.cls("-disabled"),
            cssPageLinkContainer(cssPageIcon("Board"), cssLinkText(t("Examples & Templates")), testId("dm-templates-page"), railCard("dm-templates-page")),
          ] : [
            cssPageLink(cssPageIcon("Board"), cssLinkText(t("Examples & Templates")),
              urlState().setLinkUrl({ homePage: "templates" }),
              testId("dm-templates-page"), railCard("dm-templates-page"),
            ),
          ],
        ),
        isAnonymous ? null : cssPageEntry(
          cssPageEntry.cls("-selected", use => use(home.currentPage) === "trash"),
          cssPageLink(cssPageIcon("RemoveBig"), cssLinkText(t("Trash")),
            urlState().setLinkUrl({ homePage: "trash" }),
            testId("dm-trash"), railCard("dm-trash"),
          ),
        ),
        cssPageEntry(
          dom.show(isFeatureEnabled("tutorials") && Boolean(templateOrg && onboardingTutorialDocId)),
          isAnonymous ? [
            dom.cls("-disabled"),
            cssPageLinkContainer(cssPageIcon("Bookmark"), cssLinkText(t("Tutorial")), testId("dm-basic-tutorial"), railCard("dm-basic-tutorial")),
          ] : [
            cssPageLink(cssPageIcon("Bookmark"), cssLinkText(t("Tutorial")),
              urlState().setLinkUrl({ org: templateOrg!, doc: onboardingTutorialDocId }),
              testId("dm-basic-tutorial"), railCard("dm-basic-tutorial"),
            ),
          ],
        ),
        (isAnonymous ?
          cssPageEntry(
            dom.cls("-disabled"),
            cssPageLinkContainer(cssPageIcon("Video"), cssLinkText(t("Video Tour")), testId("dm-video-tour-placeholder")),
          ) : createVideoTourToolsButton()
        ),
        (home.app.isInstallAdmin() ?
          cssPageEntry(
            cssPageLink(cssPageIcon("Settings"), cssLinkText(getAdminPanelName()),
              urlState().setLinkUrl({ adminPanel: "admin" }),
              testId("dm-admin-panel"),
            ),
          ) : null
        ),
        (isAnonymous ?
          cssPageEntry(
            dom.cls("-disabled"),
            cssPageLinkContainer(cssPageIcon("Help"), cssLinkText(t("Help Center")), testId("dm-help-center-placeholder"), railCard("dm-help-center-placeholder")),
          ) : createHelpTools(home.app)
        ),
        (isAnonymous ?
          cssPageEntry(
            dom.cls("-disabled"),
            cssPageLinkContainer(cssPageIcon("Accessibility"), cssLinkText(t("Accessibility")), testId("dm-accessibility-placeholder"), railCard("dm-accessibility-placeholder")),
          ) : createAccessibilityTools()
        ),
        (commonUrls.termsOfService ?
          cssPageEntry(
            isAnonymous ? [
              dom.cls("-disabled"),
              cssPageLinkContainer(cssPageIcon("Memo"), cssLinkText(t("Terms of service")), testId("dm-tos"), railCard("dm-tos")),
            ] : [
              cssPageLink(cssPageIcon("Memo"), cssLinkText(t("Terms of service")),
                { href: commonUrls.termsOfService, target: "_blank" },
                testId("dm-tos"), railCard("dm-tos"),
              ),
            ],
          ) : null
        ),
      ),
    ),
    // Settings in the lower-left corner, outside the scroll, as in every KNet service. It opens
    // inside this frame, on the last workspace opened (openspec extend-model-providers, specs/settings-page).
    cssSettingsFooter(
      dom.cls(`${cssTools.className}-collapsed`, use => !use(leftPanelOpen)),
      dom.autoDispose(home.currentWSId.addListener(rememberSettingsWorkspace)),
      (_el: Element) => rememberSettingsWorkspace(home.currentWSId.get()),
      cssPageEntry(
        cssPageEntry.cls("-selected", use => use(home.currentPage) === "settings"),
        isAnonymous ? [
          dom.cls("-disabled"),
          cssPageLinkContainer(knetNavIconTile("settings"), cssLinkText(t("Settings")), testId("dm-settings")),
        ] : [
          cssPageLink(knetNavIconTile("settings"), cssLinkText(t("Settings")),
            urlState().setLinkUrl({ homePage: "settings" }),
            testId("dm-settings"),
          ),
        ],
      ),
    ),
  );
}

function addMenu(home: HomeModel, creating: Observable<boolean>): DomElementArg[] {
  const org = home.app.currentOrg;
  const orgAccess: roles.Role | null = org ? org.access : null;
  const needUpgrade = home.app.currentFeatures?.maxWorkspacesPerOrg === 1;
  // "Database view" = workspace page OR onlyShowDocuments pref is true.
  // "Mission Control" = the "all" home page with onlyShowDocuments = false.
  const currentPage = home.currentPage.get();
  const inMissionControl = currentPage === "all" && !home.onlyShowDocuments.get();

  if (inMissionControl) {
    // Mission Control mode: create any type of item.
    return [
      upgradableMenuItem(needUpgrade, () => creating.set(true),
        knetIconTile("workspace"), t("Create Workspace"),
        dom.cls("disabled", use => !roles.canEdit(orgAccess) || !use(home.available)),
        testId("dm-new-workspace"),
      ),
      upgradeText(needUpgrade, () => home.app.showUpgradeModal()),
      menuItem(() => {
        const wsId = home.currentWSId.get();
        const orgId = home.effectiveOrgId;
        const url = orgId && wsId ? `/flows/o/${orgId}/ws/${wsId}/knets/` : "/flows/knets";
        window.open(url, "_blank");
      },
      knetIconTile("knets"), t("New K-Net"),
      testId("dm-new-agent"),
      ),
      menuItem(() => {
        const wsId = home.currentWSId.get();
        const orgId = home.effectiveOrgId;
        const url = orgId && wsId ? `/flows/o/${orgId}/ws/${wsId}/flows/` : "/flows/flows";
        window.open(url, "_blank");
      },
      knetIconTile("flows"), t("New Flow"),
      testId("dm-new-flow"),
      ),
      menuItem(() => newDocMethods.createDocAndOpen(home),
        knetIconTile("databases"), t("New Database"),
        dom.cls("disabled", !home.newDocWorkspace.get()),
        testId("dm-new-doc"),
      ),
      menuItem(() => { window.open("/maps", "_blank"); },
        knetIconTile("maps"), t("New Map"),
        testId("dm-new-map"),
      ),
      menuItem(() => {
        const wsId = home.currentWSId.get();
        const orgId = home.effectiveOrgId;
        const url = orgId && wsId ? `/knowledgebases/o/${orgId}/ws/${wsId}/collections/` : "/knowledgebases/";
        window.open(url, "_blank");
      },
      knetIconTile("knowledgebases"), t("New Knowledge Base"),
      testId("dm-new-collection"),
      ),
      menuItem(() => {
        const wsId = home.currentWSId.get();
        const orgId = home.effectiveOrgId;
        const url = orgId && wsId ? `/connectors/o/${orgId}/ws/${wsId}/available` : "/connectors";
        window.open(url, "_blank");
      },
      knetIconTile("connectors"), t("New App"),
      testId("dm-new-tool"),
      ),
    ];
  }

  // Table mode: create/import tables, folders, workflows
  return [
    menuItem(() => newDocMethods.createDocAndOpen(home),
      menuIcon("Page"), t("Create empty database"),
      dom.cls("disabled", !home.newDocWorkspace.get()),
      testId("dm-new-doc"),
    ),
    menuItem(() => newDocMethods.importDocAndOpen(home),
      menuIcon("Import"), t("Import database"),
      dom.cls("disabled", !home.newDocWorkspace.get()),
      testId("dm-import"),
    ),
    domComputed(home.importSources, importSources => ([
      ...importSources.map((source, i) =>
        menuItem(() => newDocMethods.importFromPluginAndOpen(home, source),
          menuIcon("Import"),
          source.importSource.label,
          dom.cls("disabled", !home.newDocWorkspace.get()),
          testId(`dm-import-plugin`),
        )),
    ])),
  ];
}

function workspaceMenu(home: HomeModel, ws: Workspace, renaming: Observable<Workspace | null>) {
  function deleteWorkspace() {
    confirmModal(t("Delete {{workspace}} and all included documents?", { workspace: ws.name }), t("Delete"),
      async () => {
        let all = home.workspaces.get();
        const index = all.findIndex(w => w.id === ws.id);
        const selected = home.currentWSId.get() === ws.id;
        await home.deleteWorkspace(ws.id, false);
        // If workspace was not selected, don't do navigation.
        if (!selected) { return; }
        all = home.workspaces.get();
        if (!all.length) {
          // There was only one workspace, navigate to all docs.
          await urlState().pushUrl({ homePage: "all" });
        }
        else {
          // Maintain the index.
          const newIndex = Math.max(0, Math.min(index, all.length - 1));
          await urlState().pushUrl({ ws: all[newIndex].id });
        }
      },
      { explanation: t("Workspace will be moved to Trash.") });
  }

  async function manageWorkspaceUsers() {
    const api = home.app.api;
    const user = home.app.currentUser;
    (await loadUserManager()).showUserManagerModal(api, {
      permissionData: api.getWorkspaceAccess(ws.id),
      activeUser: user,
      resourceType: "workspace",
      resourceId: ws.id,
      resource: ws,
    });
  }

  const needUpgrade = home.app.currentFeatures?.maxWorkspacesPerOrg === 1;

  return [
    upgradableMenuItem(needUpgrade, () => renaming.set(ws), t("Rename"),
      dom.cls("disabled", !roles.canEdit(ws.access)),
      testId("dm-rename-workspace")),
    upgradableMenuItem(needUpgrade, deleteWorkspace, t("Delete"),
      dom.cls("disabled", user => !roles.canEdit(ws.access)),
      testId("dm-delete-workspace")),
    // TODO: Personal plans can't currently share workspaces, but that restriction
    // should formally be documented and defined in `Features`, with this check updated
    // to look there instead.
    home.app.isPersonal ? null : upgradableMenuItem(needUpgrade, manageWorkspaceUsers,
      roles.canEditAccess(ws.access) ? t("Manage users") : t("Access Details"),
      testId("dm-workspace-access")),
    upgradeText(needUpgrade, () => home.app.showUpgradeModal()),
  ];
}

// Below are all the styled elements.

const cssContent = styled(cssLeftPanel, `
  --page-icon-margin: 12px;
`);

const cssSettingsFooter = styled("div", `
  flex: none;
  padding: 4px 0;
  border-top: 1px solid ${theme.pagePanelsBorder};
`);

const cssSubEntry = styled(cssPageEntry, `
  & > .${cssPageLink.className}, & > .${cssPageLinkContainer.className}, & > .${cssPageButton.className} {
    padding-left: 12px;
  }
`);

// Workspace sections (header + sub-entries) are nested, so the shared collapse
// rules can't reach them — hide whole sections in the folded 48px rail and show
// only top-level items (Overview, Channels, tools).
const cssWsSection = styled("div", `
  .${cssTools.className}-collapsed & {
    display: none;
  }
`);

const cssDotsIcon = styled("div", `
  margin-left: auto;
  margin-right: 8px;
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  --icon-color: ${theme.lightText};

  &:hover {
    background-color: ${theme.hover};
    --icon-color: ${theme.text};
  }
`);

const cssToggleIcon = styled("div", `
  margin-right: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  --icon-color: ${theme.lightText};

  &:hover {
    background-color: ${theme.hover};
    --icon-color: ${theme.text};
  }
`);

// Workspace name in the section header — acts as a clickable link to Mission Control filtered view.
const cssWorkspaceHeaderLink = styled(cssSectionHeaderText, `
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    color: ${theme.controlFg};
    text-decoration: underline;
  }
`);

export const cssEditorInput = styled(transientInput, `
  height: 24px;
  flex: 1 1 0px;
  min-width: 0px;
  background-color: ${theme.inputBg};
  margin-right: 16px;
  font-size: inherit;
`);
